import React, { useEffect, useCallback } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';

/**
 * Componente que monitora alterações não salvas e exibe confirmação ao sair
 * Uso: Envolva o conteúdo do formulário com este componente
 */
export default function UnsavedChangesDialog({ 
  hasUnsavedChanges, 
  onConfirmLeave, 
  isOpen, 
  onOpenChange,
  title = "Deseja realmente sair?",
  description = "Você tem alterações não salvas. Se sair agora, todas as alterações serão perdidas."
}) {
  return (
    <AlertDialog open={isOpen} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="text-red-600">{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancelar</AlertDialogCancel>
          <AlertDialogAction 
            onClick={onConfirmLeave}
            className="bg-red-600 hover:bg-red-700"
          >
            Sair sem salvar
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

/**
 * Hook para gerenciar estado de alterações não salvas
 */
export function useUnsavedChanges(initialData = {}) {
  const [hasChanges, setHasChanges] = React.useState(false);
  const [showDialog, setShowDialog] = React.useState(false);
  const [pendingAction, setPendingAction] = React.useState(null);

  // Marcar como tendo alterações
  const markAsChanged = useCallback(() => {
    setHasChanges(true);
  }, []);

  // Resetar estado (após salvar)
  const resetChanges = useCallback(() => {
    setHasChanges(false);
  }, []);

  // Tentar executar uma ação que pode ser bloqueada
  const tryAction = useCallback((action) => {
    if (hasChanges) {
      setPendingAction(() => action);
      setShowDialog(true);
    } else {
      action();
    }
  }, [hasChanges]);

  // Confirmar saída
  const confirmLeave = useCallback(() => {
    setShowDialog(false);
    setHasChanges(false);
    if (pendingAction) {
      pendingAction();
      setPendingAction(null);
    }
  }, [pendingAction]);

  // Cancelar saída
  const cancelLeave = useCallback(() => {
    setShowDialog(false);
    setPendingAction(null);
  }, []);

  // Aviso ao fechar a página/aba
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasChanges) {
        e.preventDefault();
        e.returnValue = '';
        return '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasChanges]);

  return {
    hasChanges,
    showDialog,
    markAsChanged,
    resetChanges,
    tryAction,
    confirmLeave,
    cancelLeave,
    setShowDialog
  };
}
